<template>
  <v-dialog :value="value" fullscreen hide-overlay transition="dialog-bottom-transition" @input="$emit('input', $event)">
    <section class="watch-detail-shell">
      <header class="watch-detail-header">
        <v-btn icon color="white" aria-label="Watch-Training schliessen" @click="$emit('input', false)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <div class="watch-detail-heading">
          <span>{{ watchDeviceName.toUpperCase() }} ANALYSE</span>
          <h2>{{ activity ? activity.title : "Watch-Training" }}</h2>
          <small>{{ dateLabel }}</small>
        </div>
        <div class="watch-detail-live-mark"><v-icon small color="#8fffc6">mdi-watch-variant</v-icon></div>
      </header>

      <main v-if="activity" class="watch-detail-content">
        <section class="watch-detail-hero">
          <div class="watch-detail-orbit"><v-icon color="#ff6079">mdi-heart-pulse</v-icon></div>
          <div>
            <span>Ø HERZFREQUENZ</span>
            <strong>{{ health.averageHeartRate }} <small>BPM</small></strong>
          </div>
          <button v-if="health.timelineSamples && health.timelineSamples.length > 1" type="button" @click="$emit('open-pulse', activity)">
            Pulsverlauf <v-icon x-small color="white">mdi-arrow-expand-all</v-icon>
          </button>
        </section>

        <section class="watch-detail-metric-grid">
          <article><v-icon color="#62d9ff">mdi-timer-outline</v-icon><strong>{{ health.duration }}</strong><span>Dauer</span></article>
          <article><v-icon color="#ff6079">mdi-heart-flash</v-icon><strong>{{ health.maximumHeartRate }}</strong><span>Max BPM</span></article>
          <article><v-icon color="#ff9f43">mdi-fire</v-icon><strong>{{ health.calories }}</strong><span>Aktive kcal</span></article>
        </section>

        <section v-if="zoneRows.length" class="watch-detail-card">
          <div class="watch-detail-section-title"><v-icon small color="#8fffc6">mdi-chart-timeline-variant-shimmer</v-icon>Pulszonen</div>
          <div v-for="zone in zoneRows" :key="zone.zone" class="watch-zone-row">
            <span :style="{ color: zone.color }">Z{{ zone.zone }}</span>
            <div class="watch-zone-track"><i :style="{ width: zone.percent + '%', background: zone.color }"></i></div>
            <strong>{{ formatSeconds(zone.durationSeconds) }}</strong>
          </div>
          <small v-if="health.configuredMaximumHeartRate" class="watch-detail-footnote">
            Berechnet mit Maximalpuls {{ health.configuredMaximumHeartRate }} BPM
          </small>
        </section>

        <section v-if="health.recovery" class="watch-detail-card watch-recovery-card">
          <div class="watch-detail-section-title"><v-icon small color="#62d9ff">mdi-heart-cog-outline</v-icon>Erholung nach 1 Minute</div>
          <strong v-if="health.recovery">−{{ Math.round(health.recovery.oneMinuteDropBPM) }} BPM</strong>
          <p v-if="health.recovery">Automatisch aus {{ healthSourceName }} ergänzt.</p>
          <p v-else>Für dieses Training liegt noch kein Recovery-Messwert vor.</p>
        </section>

        <section v-if="achievements.length" class="watch-detail-card watch-achievement-card">
          <div class="watch-detail-section-title"><v-icon small color="#ffd54f">mdi-trophy</v-icon>Bestleistungen</div>
          <div v-for="achievement in achievements" :key="achievement.id" class="watch-achievement-row">
            <v-icon small color="#ffd54f">mdi-sparkles</v-icon>
            <div><strong>{{ achievement.title }}</strong><span>{{ achievement.detail }}</span></div>
          </div>
        </section>

        <section v-if="exercises.length" class="watch-detail-exercises">
          <div class="watch-detail-section-title"><v-icon small color="#8fffc6">mdi-dumbbell</v-icon>Übungen & Sätze</div>
          <article v-for="(exercise, exerciseIndex) in exercises" :key="exercise.instanceId || exerciseIndex" class="watch-exercise-card">
            <div class="watch-exercise-head">
              <span>{{ String(exerciseIndex + 1).padStart(2, "0") }}</span>
              <div><strong>{{ exercise.title || "Übung" }}</strong><small>{{ exercisePulse(exercise) }}</small></div>
            </div>
            <div v-if="exercise.sets && exercise.sets.length" class="watch-set-list">
              <div v-for="(set, setIndex) in exercise.sets" :key="setIndex" class="watch-set-row">
                <span>S{{ set.position || setIndex + 1 }}</span>
                <strong>{{ setValue(set) }}</strong>
                <small v-if="set.actualRestSeconds !== null && set.actualRestSeconds !== undefined">
                  Pause {{ formatSeconds(set.actualRestSeconds) }}
                  <template v-if="set.heartRateAtRestEnd"> · {{ set.heartRateAtRestEnd }} BPM</template>
                </small>
              </div>
            </div>
          </article>
        </section>

        <button v-if="health.timelineSamples && health.timelineSamples.length > 1" type="button" class="watch-detail-chart-cta" @click="$emit('open-pulse', activity)">
          <v-icon color="white">mdi-chart-bell-curve-cumulative</v-icon>
          <span><strong>Interaktiven Pulsverlauf öffnen</strong><small>{{ activity.isGroupFitness ? "Deine Herzfrequenz während des Kurses" : "Mit Übungen, Sätzen und Zeitmarken" }}</small></span>
          <v-icon color="#8fffc6">mdi-chevron-right</v-icon>
        </button>
      </main>
    </section>
  </v-dialog>
</template>

<script>
import { watchDeviceLabel, watchHealthSourceLabel } from "@/services/watchPlatform"

export default {
  name: "WatchWorkoutDetail",
  props: {
    value: { type: Boolean, default: false },
    activity: { type: Object, default: null },
  },
  computed: {
    health() {
      return this.activity?.watchHealth || {}
    },
    watchData() {
      return this.activity?.workout?.watchData || {}
    },
    watchDeviceName() {
      return watchDeviceLabel(this.activity?.workout?.source, this.watchData.platform)
    },
    healthSourceName() {
      return watchHealthSourceLabel(this.activity?.workout?.source, this.watchData.platform)
    },
    exercises() {
      return Array.isArray(this.watchData.exercises) ? this.watchData.exercises : []
    },
    achievements() {
      return Array.isArray(this.watchData.achievements) ? this.watchData.achievements : []
    },
    dateLabel() {
      const timestamp = Number(this.activity?.workout?.ts)
      return Number.isFinite(timestamp) && timestamp > 0
        ? this.$moment(timestamp * 1000).format("DD. MMMM YYYY · HH:mm")
        : "Gespeichertes Training"
    },
    zoneRows() {
      const palette = { 1: "#37d7ff", 2: "#00c763", 3: "#ffd54f", 4: "#ff9800", 5: "#ff3b4d" }
      const zones = Array.isArray(this.health.heartRateZones) ? this.health.heartRateZones : []
      const total = zones.reduce((sum, zone) => sum + Math.max(0, Number(zone.durationSeconds) || 0), 0)
      return zones
        .map((zone) => ({
          zone: Number(zone.zone),
          durationSeconds: Math.max(0, Number(zone.durationSeconds) || 0),
          percent: total > 0 ? Math.max(2, ((Number(zone.durationSeconds) || 0) / total) * 100) : 0,
          color: palette[Number(zone.zone)] || "#8a94a3",
        }))
        .filter((zone) => zone.durationSeconds > 0)
        .sort((left, right) => right.zone - left.zone)
    },
  },
  methods: {
    formatSeconds(seconds) {
      const total = Math.max(0, Math.round(Number(seconds) || 0))
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const rest = total % 60
      return hours > 0
        ? `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`
        : `${minutes}:${String(rest).padStart(2, "0")}`
    },
    exercisePulse(exercise) {
      const average = Number(exercise?.health?.averageHeartRate)
      return Number.isFinite(average) && average > 0 ? `Ø ${Math.round(average)} BPM` : `${exercise?.sets?.length || 0} Sätze`
    },
    setValue(set) {
      const parts = []
      if (Number(set.reps) > 0) parts.push(`${Math.round(Number(set.reps))} Wdh.`)
      if (Number(set.weightKg) > 0) parts.push(`${Number(set.weightKg).toLocaleString("de-CH", { maximumFractionDigits: 1 })} kg`)
      if (Number(set.durationSeconds) > 0) parts.push(this.formatSeconds(set.durationSeconds))
      if (Number(set.distanceMeters) > 0) parts.push(`${Math.round(Number(set.distanceMeters))} m`)
      return parts.join(" · ") || "Erledigt"
    },
  },
}
</script>

<style scoped>
.watch-detail-shell { min-height: 100vh; color: #fff; background: radial-gradient(circle at 84% 6%, rgba(0, 198, 99, .18), transparent 28%), radial-gradient(circle at 12% 22%, rgba(255, 65, 91, .13), transparent 30%), #080a0d; }
.watch-detail-header { position: sticky; top: 0; z-index: 5; display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid rgba(255,255,255,.08); background: rgba(8,10,13,.88); backdrop-filter: blur(22px); }
.watch-detail-heading { min-width: 0; flex: 1; }
.watch-detail-heading span { color: #8fffc6; font-size: .62rem; font-weight: 900; letter-spacing: .12em; }
.watch-detail-heading h2 { overflow: hidden; margin: 1px 0; font-size: 1.05rem; white-space: nowrap; text-overflow: ellipsis; }
.watch-detail-heading small { color: #7f8a98; }
.watch-detail-live-mark { display: grid; width: 36px; height: 36px; place-items: center; border: 1px solid rgba(143,255,198,.2); border-radius: 50%; background: rgba(0,198,99,.12); }
.watch-detail-content { width: min(760px, 100%); margin: 0 auto; padding: 18px 14px 40px; }
.watch-detail-hero { display: flex; align-items: center; gap: 13px; padding: 17px; border: 1px solid rgba(255,96,121,.22); border-radius: 22px; background: linear-gradient(135deg, rgba(255,96,121,.16), rgba(255,255,255,.035)); }
.watch-detail-orbit { display: grid; width: 48px; height: 48px; place-items: center; border-radius: 50%; background: rgba(255,96,121,.13); box-shadow: 0 0 28px rgba(255,96,121,.18); }
.watch-detail-hero > div:nth-child(2) { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.watch-detail-hero span { color: #aab2bd; font-size: .62rem; font-weight: 800; letter-spacing: .08em; }
.watch-detail-hero strong { font-size: 1.65rem; line-height: 1.1; }
.watch-detail-hero strong small { color: #ff7188; font-size: .65rem; }
.watch-detail-hero button { padding: 8px 10px; color: #fff; font-size: .66rem; font-weight: 800; border: 1px solid rgba(255,255,255,.12); border-radius: 12px; background: rgba(255,255,255,.08); }
.watch-detail-metric-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 10px; }
.watch-detail-metric-grid article { display: flex; min-width: 0; flex-direction: column; align-items: center; padding: 12px 5px; border: 1px solid rgba(255,255,255,.07); border-radius: 16px; background: rgba(255,255,255,.045); }
.watch-detail-metric-grid strong { margin-top: 5px; font-size: 1rem; }
.watch-detail-metric-grid span { color: #7f8a98; font-size: .6rem; font-weight: 700; }
.watch-detail-card { margin-top: 11px; padding: 14px; border: 1px solid rgba(255,255,255,.07); border-radius: 18px; background: rgba(255,255,255,.045); }
.watch-detail-section-title { display: flex; align-items: center; gap: 7px; margin-bottom: 10px; font-size: .75rem; font-weight: 900; letter-spacing: .04em; text-transform: uppercase; }
.watch-zone-row { display: grid; grid-template-columns: 22px 1fr 42px; align-items: center; gap: 8px; margin: 7px 0; }
.watch-zone-row > span { font-size: .67rem; font-weight: 900; }
.watch-zone-row > strong { color: #aab2bd; font-size: .62rem; text-align: right; font-variant-numeric: tabular-nums; }
.watch-zone-track { height: 7px; overflow: hidden; border-radius: 99px; background: rgba(255,255,255,.07); }
.watch-zone-track i { display: block; height: 100%; border-radius: inherit; }
.watch-detail-footnote { display: block; margin-top: 9px; color: #707986; font-size: .6rem; }
.watch-recovery-card > strong { color: #62d9ff; font-size: 1.55rem; }
.watch-recovery-card p { margin: 3px 0 0; color: #8f99a6; font-size: .7rem; }
.watch-achievement-card { border-color: rgba(255,213,79,.18); background: linear-gradient(135deg, rgba(255,213,79,.09), rgba(255,255,255,.035)); }
.watch-achievement-row { display: flex; align-items: center; gap: 9px; margin-top: 8px; }
.watch-achievement-row div { display: flex; flex-direction: column; }
.watch-achievement-row strong { font-size: .72rem; }
.watch-achievement-row span { color: #ffd54f; font-size: .66rem; font-weight: 800; }
.watch-detail-exercises { margin-top: 17px; }
.watch-exercise-card { margin-top: 8px; overflow: hidden; border: 1px solid rgba(255,255,255,.07); border-radius: 17px; background: rgba(255,255,255,.04); }
.watch-exercise-head { display: flex; align-items: center; gap: 10px; padding: 11px; }
.watch-exercise-head > span { display: grid; width: 31px; height: 31px; place-items: center; color: #8fffc6; font-size: .65rem; font-weight: 900; border-radius: 10px; background: rgba(0,198,99,.12); }
.watch-exercise-head > div { display: flex; min-width: 0; flex-direction: column; }
.watch-exercise-head strong { overflow: hidden; font-size: .76rem; white-space: nowrap; text-overflow: ellipsis; }
.watch-exercise-head small { color: #7f8a98; font-size: .62rem; }
.watch-set-list { border-top: 1px solid rgba(255,255,255,.06); }
.watch-set-row { display: grid; grid-template-columns: 28px minmax(75px, 1fr) auto; align-items: center; gap: 7px; padding: 8px 11px; border-bottom: 1px solid rgba(255,255,255,.045); }
.watch-set-row:last-child { border-bottom: 0; }
.watch-set-row > span { color: #8fffc6; font-size: .62rem; font-weight: 900; }
.watch-set-row > strong { font-size: .68rem; }
.watch-set-row > small { color: #ff9f43; font-size: .58rem; text-align: right; }
.watch-detail-chart-cta { width: 100%; display: flex; align-items: center; gap: 10px; margin-top: 13px; padding: 13px; color: #fff; text-align: left; border: 1px solid rgba(143,255,198,.17); border-radius: 17px; background: linear-gradient(120deg, rgba(0,198,99,.15), rgba(255,255,255,.04)); }
.watch-detail-chart-cta span { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.watch-detail-chart-cta strong { font-size: .73rem; }
.watch-detail-chart-cta small { color: #80908a; font-size: .6rem; }
@media (max-width: 390px) { .watch-detail-hero button { font-size: 0; } .watch-detail-hero button .v-icon { margin: 0; } .watch-set-row { grid-template-columns: 25px 1fr; } .watch-set-row > small { grid-column: 2; text-align: left; } }
</style>
